import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Users, BookOpen, Activity, LogOut, Loader2, Shield,
  BarChart3, Settings, ChevronRight, ChevronLeft, Zap,
  Clock, AlertTriangle, XCircle, RefreshCw, Server, Eye, Lock,
  Check, X, MessageSquare, Search, Brain, TrendingUp,
  Play, Circle, Hash, Database, FileText, CheckCircle2,
  Layers, PenLine, ArrowRight, DollarSign, Cpu, ToggleLeft, ToggleRight,
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import toast from 'react-hot-toast'

const API_BASE     = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'

// ── Status config ──────────────────────────────────────────────────────────────

const CH_STATUS = {
  NOT_STARTED: { label: 'Not Started', color: '#64748b', bg: '#f1f5f9' },
  GENERATING:  { label: 'Generating',  color: '#2563eb', bg: '#dbeafe', pulse: true },
  DRAFT:       { label: 'Draft',       color: '#7c3aed', bg: '#ede9fe' },
  REVIEW:      { label: 'Under Review',color: '#d97706', bg: '#fef3c7' },
  APPROVED:    { label: 'Approved',    color: '#16a34a', bg: '#dcfce7' },
  LOCKED:      { label: 'Locked',      color: '#0f172a', bg: '#e2e8f0' },
}

const RISK_CONFIG = {
  active:  { label: 'Generating', color: '#2563eb', bg: '#dbeafe' },
  high:    { label: 'At Risk',    color: '#dc2626', bg: '#fee2e2' },
  medium:  { label: 'In Progress',color: '#d97706', bg: '#fef3c7' },
  low:     { label: 'On Track',   color: '#16a34a', bg: '#dcfce7' },
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function completionPct(chapters) {
  if (!chapters?.length) return 0
  const done = chapters.filter(c => ['DRAFT', 'APPROVED', 'LOCKED'].includes(c.status)).length
  return Math.round((done / chapters.length) * 100)
}

function totalWords(chapters) {
  return (chapters || []).reduce((sum, c) => sum + (c.word_count || 0), 0)
}

function lastActive(chapters) {
  if (!chapters?.length) return null
  const dates = chapters.map(c => c.updated_at || c.created_at).filter(Boolean)
  if (!dates.length) return null
  return new Date(Math.max(...dates.map(d => new Date(d).getTime())))
}

function relTime(date) {
  if (!date) return '—'
  const d = date instanceof Date ? date : new Date(date)
  if (isNaN(d)) return '—'
  const mins = Math.floor((Date.now() - d) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

function fmtWords(n) {
  if (!n) return '0'
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`
}

function riskOf(chapters) {
  if (!chapters?.length) return 'low'
  if (chapters.some(c => c.status === 'GENERATING')) return 'active'
  const pct = completionPct(chapters)
  if (pct < 30) return 'high'
  if (pct < 70) return 'medium'
  return 'low'
}

// ── Small UI atoms ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = CH_STATUS[status] || CH_STATUS.NOT_STARTED
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-sans font-medium"
      style={{ fontSize: 11, color: cfg.color, background: cfg.bg }}
    >
      {cfg.pulse ? (
        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: cfg.color, animation: 'pulse 1.4s ease-in-out infinite' }} />
      ) : (
        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: cfg.color }} />
      )}
      {cfg.label}
    </span>
  )
}

function RiskBadge({ chapters }) {
  const risk = riskOf(chapters)
  const cfg  = RISK_CONFIG[risk]
  return (
    <span className="px-2 py-0.5 rounded-full font-sans font-medium"
      style={{ fontSize: 11, color: cfg.color, background: cfg.bg }}>
      {cfg.label}
    </span>
  )
}

function StatCard({ icon: Icon, label, value, sub, color, bg, onClick }) {
  return (
    <div
      className="rounded-2xl p-5 transition-all"
      style={{ background: '#fff', border: '1px solid #e2e8f0', cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: bg }}>
          <Icon style={{ width: 17, height: 17, color }} />
        </div>
        <span className="font-sans text-sm font-medium" style={{ color: '#475569' }}>{label}</span>
      </div>
      <p className="font-mono text-3xl font-semibold" style={{ color: '#0f172a' }}>{value ?? '—'}</p>
      {sub && <p className="font-sans text-xs mt-1" style={{ color: '#94a3b8' }}>{sub}</p>}
    </div>
  )
}

// ── Sidebar nav ─────────────────────────────────────────────────────────────────

const NAV = [
  { icon: BarChart3,    label: 'Overview',    path: '/admin' },
  { icon: Users,        label: 'Candidates',  path: '/admin/candidates' },
  { icon: Zap,          label: 'Gen Queue',   path: '/admin/queue' },
  { icon: CheckCircle2, label: 'Reviews',     path: '/admin/reviews' },
  { icon: Activity,     label: 'Activity',    path: '/admin/activity' },
  { icon: DollarSign,   label: 'AI Costs',    path: '/admin/costs' },
  { icon: Cpu,          label: 'AI Monitor',  path: '/admin/monitor' },
  { icon: Server,       label: 'System',      path: '/admin/system' },
  { icon: Settings,     label: 'Settings',    path: '/admin/settings' },
]

function AdminLayout({ session, onLogout, children }) {
  const location = useLocation()
  const isActive = (path) => path === '/admin'
    ? location.pathname === '/admin' || location.pathname === '/admin/'
    : location.pathname.startsWith(path)

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#f1f5f9' }}>
      {/* Sidebar */}
      <aside className="flex flex-col w-52 shrink-0" style={{ background: '#0f172a', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center gap-2.5 h-14 px-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0"
            style={{ background: '#dc2626', color: '#fff' }}>A</div>
          <div>
            <p className="font-sans text-sm font-semibold" style={{ color: '#f1f5f9' }}>AcdyON Admin</p>
            <p className="font-mono" style={{ color: '#475569', fontSize: 9, letterSpacing: '0.08em' }}>CONTROL PANEL</p>
          </div>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV.map(({ icon: Icon, label, path }) => {
            const active = isActive(path)
            return (
              <a key={path} href={path}
                onClick={e => { e.preventDefault(); window.history.pushState({}, '', path); window.dispatchEvent(new PopStateEvent('popstate')) }}
                className="flex items-center gap-2.5 transition-all duration-150"
                style={{
                  padding: '8px 10px', margin: '1px 6px', borderRadius: 8,
                  color: active ? '#93c5fd' : '#64748b',
                  background: active ? 'rgba(220,38,38,0.15)' : 'transparent',
                  borderLeft: active ? '2px solid #ef4444' : '2px solid transparent',
                  textDecoration: 'none',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
              >
                <Icon style={{ width: 15, height: 15, color: active ? '#f87171' : '#475569' }} />
                <span className="font-sans text-sm" style={{ fontWeight: active ? 500 : 400 }}>{label}</span>
              </a>
            )
          })}
        </nav>

        <div className="p-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
              style={{ background: '#dc2626', color: '#fff' }}>
              {(session?.user?.email || 'A')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-sans text-xs font-semibold truncate" style={{ color: '#e2e8f0' }}>
                {session?.user?.email?.split('@')[0] || 'Admin'}
              </p>
              <p className="font-mono" style={{ color: '#475569', fontSize: 9, letterSpacing: '0.06em' }}>ADMINISTRATOR</p>
            </div>
            <button onClick={onLogout} title="Sign out"
              className="p-1.5 rounded-lg transition-colors shrink-0" style={{ color: '#475569' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.15)'; e.currentTarget.style.color = '#f87171' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#475569' }}>
              <LogOut style={{ width: 13, height: 13 }} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 overflow-y-auto">
        {children}
      </div>
    </div>
  )
}

// ── Overview tab ───────────────────────────────────────────────────────────────

function OverviewTab({ candidates, allChapters, allTheses, onNavigate }) {
  const generating  = allChapters.filter(c => c.status === 'GENERATING')
  const drafts      = allChapters.filter(c => c.status === 'DRAFT')
  const approved    = allChapters.filter(c => c.status === 'APPROVED')
  const totalW      = totalWords(allChapters)
  const avgPct      = candidates.length
    ? Math.round(candidates.reduce((s, c) => s + completionPct(c.allChapters), 0) / candidates.length)
    : 0

  const recentActivity = [...allChapters]
    .filter(c => c.updated_at)
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
    .slice(0, 12)
    .map(ch => {
      const thesis    = allTheses.find(t => t.id === ch.thesis_id)
      const candidate = candidates.find(c => c.theses.some(t => t.id === ch.thesis_id))
      return { ch, thesis, candidate }
    })

  return (
    <div className="p-8 space-y-8 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold" style={{ color: '#0f172a', fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}>
            Platform Overview
          </h1>
          <p className="font-sans text-sm mt-1" style={{ color: '#94a3b8' }}>
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users}         label="Active Candidates"  value={candidates.length}   color="#2563eb" bg="#dbeafe" onClick={() => onNavigate('/admin/candidates')} />
        <StatCard icon={BookOpen}      label="Total Theses"       value={allTheses.length}    color="#7c3aed" bg="#ede9fe" />
        <StatCard icon={Zap}           label="Generating Now"     value={generating.length}   color="#d97706" bg="#fef3c7" onClick={() => onNavigate('/admin/queue')} />
        <StatCard icon={CheckCircle2}  label="Pending Review"     value={drafts.length}       color="#dc2626" bg="#fee2e2" onClick={() => onNavigate('/admin/reviews')} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp}    label="Chapters Approved"  value={approved.length}     color="#16a34a" bg="#dcfce7" />
        <StatCard icon={Hash}          label="Total Words"        value={`${fmtWords(totalW)}`} sub="across all chapters" color="#0369a1" bg="#e0f2fe" />
        <StatCard icon={Layers}        label="Total Chapters"     value={allChapters.length}  color="#475569" bg="#f1f5f9" />
        <StatCard icon={PenLine}       label="Avg Completion"     value={`${avgPct}%`}        sub="across all theses"   color="#059669" bg="#d1fae5" />
      </div>

      {/* Recent activity */}
      <div className="rounded-2xl overflow-hidden" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid #e2e8f0' }}>
          <h2 className="font-sans font-semibold text-sm" style={{ color: '#0f172a' }}>Recent Chapter Activity</h2>
          <span className="font-mono text-xs" style={{ color: '#94a3b8' }}>{allChapters.length} total chapters</span>
        </div>
        {recentActivity.length === 0 ? (
          <div className="text-center py-10">
            <Activity style={{ width: 28, height: 28, color: '#cbd5e1', margin: '0 auto 10px' }} />
            <p className="font-sans text-sm" style={{ color: '#94a3b8' }}>No activity yet.</p>
          </div>
        ) : (
          <div>
            {recentActivity.map(({ ch, thesis, candidate }, i) => (
              <div key={ch.id} className="flex items-center gap-4 px-6 py-3"
                style={{ borderBottom: i < recentActivity.length - 1 ? '1px solid #f8fafc' : 'none' }}>
                <StatusBadge status={ch.status} />
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm truncate" style={{ color: '#0f172a' }}>
                    <span className="font-medium">{candidate?.full_name || 'Unknown'}</span>
                    {' — '}{thesis?.title ? thesis.title.slice(0, 40) + (thesis.title.length > 40 ? '…' : '') : 'Unknown thesis'}
                  </p>
                  <p className="font-sans text-xs mt-0.5" style={{ color: '#94a3b8' }}>
                    Chapter {ch.chapter_number}: {ch.title}
                    {ch.word_count ? ` · ${fmtWords(ch.word_count)} words` : ''}
                  </p>
                </div>
                <span className="font-mono text-xs shrink-0" style={{ color: '#cbd5e1' }}>
                  {relTime(ch.updated_at || ch.created_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Candidates tab ─────────────────────────────────────────────────────────────

function CandidatesTab({ candidates, onSelect }) {
  const [search,    setSearch]    = useState('')
  const [filter,    setFilter]    = useState('all')

  const FILTERS = [
    { id: 'all',        label: 'All' },
    { id: 'phd',        label: 'PhD' },
    { id: 'dba',        label: 'DBA' },
    { id: 'generating', label: 'Generating' },
    { id: 'review',     label: 'Needs Review' },
    { id: 'high',       label: 'At Risk' },
  ]

  const filtered = candidates.filter(c => {
    const matchSearch = !search ||
      c.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.research_topic?.toLowerCase().includes(search.toLowerCase()) ||
      c.university?.toLowerCase().includes(search.toLowerCase())

    const matchFilter =
      filter === 'all'        ? true :
      filter === 'phd'        ? c.programme === 'PhD' :
      filter === 'dba'        ? c.programme === 'DBA' :
      filter === 'generating' ? c.allChapters.some(ch => ch.status === 'GENERATING') :
      filter === 'review'     ? c.allChapters.some(ch => ch.status === 'DRAFT') :
      filter === 'high'       ? riskOf(c.allChapters) === 'high' : true

    return matchSearch && matchFilter
  })

  return (
    <div className="p-8 space-y-6 max-w-7xl">
      <div>
        <h1 className="font-display text-3xl font-bold" style={{ color: '#0f172a', fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}>
          Candidates
        </h1>
        <p className="font-sans text-sm mt-1" style={{ color: '#94a3b8' }}>{candidates.length} registered candidates</p>
      </div>

      {/* Search + filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1" style={{ minWidth: 220, maxWidth: 340 }}>
          <Search style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: '#94a3b8', pointerEvents: 'none' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name, topic, university…"
            className="w-full rounded-lg pl-8 pr-3 py-2 font-sans text-sm outline-none"
            style={{ background: '#fff', border: '1px solid #e2e8f0', color: '#0f172a' }}
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {FILTERS.map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className="px-3 py-1.5 rounded-lg font-sans text-xs font-medium transition-all"
              style={{
                background: filter === f.id ? '#0f172a' : '#fff',
                color: filter === f.id ? '#f1f5f9' : '#64748b',
                border: `1px solid ${filter === f.id ? '#0f172a' : '#e2e8f0'}`,
              }}>
              {f.label}
            </button>
          ))}
        </div>
        <span className="font-mono text-xs ml-auto" style={{ color: '#94a3b8' }}>{filtered.length} shown</span>
      </div>

      {/* Table */}
      <div className="rounded-2xl overflow-hidden" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
        {filtered.length === 0 ? (
          <div className="text-center py-14">
            <Users style={{ width: 32, height: 32, color: '#cbd5e1', margin: '0 auto 12px' }} />
            <p className="font-sans text-sm font-medium" style={{ color: '#94a3b8' }}>
              {candidates.length === 0 ? 'No candidate data. Run migration 003_admin_access.sql in Supabase.' : 'No candidates match the current filter.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-sans">
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {['Candidate', 'University', 'Degree', 'Thesis', 'Progress', 'Words', 'Status', 'Last Active', ''].map(h => (
                    <th key={h} className="text-left px-4 py-3 font-semibold whitespace-nowrap"
                      style={{ color: '#64748b', fontSize: 11, letterSpacing: '0.05em' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => {
                  const pct    = completionPct(c.allChapters)
                  const words  = totalWords(c.allChapters)
                  const active = lastActive(c.allChapters)
                  const isGen  = c.allChapters.some(ch => ch.status === 'GENERATING')
                  const hasDraft = c.allChapters.filter(ch => ch.status === 'DRAFT').length

                  return (
                    <tr key={c.id}
                      className="transition-colors cursor-pointer"
                      style={{ borderBottom: i < filtered.length - 1 ? '1px solid #f8fafc' : 'none' }}
                      onClick={() => onSelect(c)}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                            style={{ background: '#dbeafe', color: '#2563eb' }}>
                            {(c.full_name || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium" style={{ color: '#0f172a' }}>{c.full_name || '—'}</p>
                            <p className="text-xs" style={{ color: '#94a3b8' }}>{c.theses.length} thesis{c.theses.length !== 1 ? 'es' : ''}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 max-w-[140px]">
                        <p className="truncate" style={{ color: '#475569' }}>{c.university || '—'}</p>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="font-mono text-xs px-2 py-0.5 rounded-full"
                          style={{ background: '#f1f5f9', color: '#475569' }}>
                          {c.programme || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 max-w-[200px]">
                        <p className="truncate text-xs" style={{ color: '#475569' }}>
                          {c.theses[0]?.title || <span style={{ color: '#94a3b8' }}>No thesis</span>}
                        </p>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 rounded-full overflow-hidden" style={{ background: '#e2e8f0' }}>
                            <div className="h-full rounded-full transition-all"
                              style={{ width: `${pct}%`, background: pct === 100 ? '#16a34a' : '#2563eb' }} />
                          </div>
                          <span className="font-mono text-xs" style={{ color: '#64748b' }}>{pct}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 font-mono text-xs" style={{ color: '#64748b' }}>
                        {fmtWords(words)}
                      </td>
                      <td className="px-4 py-3.5">
                        <RiskBadge chapters={c.allChapters} />
                        {hasDraft > 0 && (
                          <span className="ml-1 font-mono text-xs" style={{ color: '#d97706' }}>{hasDraft} review</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 font-mono text-xs whitespace-nowrap" style={{ color: '#94a3b8' }}>
                        {relTime(active)}
                      </td>
                      <td className="px-4 py-3.5">
                        <button className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg font-sans text-xs font-medium transition-all"
                          style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}
                          onClick={e => { e.stopPropagation(); onSelect(c) }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#0f172a'; e.currentTarget.style.color = '#f1f5f9' }}
                          onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#475569' }}>
                          View <ArrowRight style={{ width: 11, height: 11 }} />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Thesis detail view ─────────────────────────────────────────────────────────

function ThesisDetailView({ candidate, allTheses, onBack, onChapterAction }) {
  const [noteOpen,   setNoteOpen]   = useState(null)
  const [noteText,   setNoteText]   = useState('')
  const [saving,     setSaving]     = useState(false)

  const theses   = candidate.theses
  const chapters = candidate.allChapters

  const thesis = theses[0] || null

  const completedChapters = chapters.filter(c => ['APPROVED', 'LOCKED'].includes(c.status)).length
  const totalTarget       = chapters.reduce((s, c) => s + (c.target_words || 8000), 0)
  const totalGenerated    = totalWords(chapters)
  const pct               = completionPct(chapters)

  const handleNoteSubmit = async (chapterId) => {
    if (!noteText.trim()) { setNoteOpen(null); return }
    setSaving(true)
    try {
      const { error } = await supabase.rpc('admin_save_chapter_note', {
        p_chapter_id: chapterId,
        p_notes: noteText,
        p_reviewed_by: 'admin',
      })
      if (error) throw error
      toast.success('Note saved')
      setNoteOpen(null)
      setNoteText('')
    } catch {
      toast.error('Failed to save note. Run 003b_admin_rpc.sql in Supabase SQL Editor.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="p-8 max-w-6xl space-y-6">
      {/* Back */}
      <button onClick={onBack} className="flex items-center gap-1.5 font-sans text-sm transition-colors"
        style={{ color: '#64748b' }}
        onMouseEnter={e => e.currentTarget.style.color = '#0f172a'}
        onMouseLeave={e => e.currentTarget.style.color = '#64748b'}>
        <ChevronLeft style={{ width: 16, height: 16 }} /> Back to Candidates
      </button>

      <div className="grid grid-cols-3 gap-6">
        {/* Candidate info card */}
        <div className="col-span-1 rounded-2xl p-5 space-y-4" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold shrink-0"
              style={{ background: '#dbeafe', color: '#2563eb' }}>
              {(candidate.full_name || '?')[0].toUpperCase()}
            </div>
            <div>
              <p className="font-display font-bold text-lg" style={{ color: '#0f172a', fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}>
                {candidate.full_name || 'Unknown'}
              </p>
              <RiskBadge chapters={candidate.allChapters} />
            </div>
          </div>
          {[
            { label: 'University',  value: candidate.university },
            { label: 'Programme',   value: candidate.programme },
            { label: 'Domain',      value: candidate.domain },
            { label: 'Submission',  value: candidate.submission_year },
            { label: 'Supervisor',  value: candidate.supervisor_name },
            { label: 'Enrolled',    value: candidate.created_at ? new Date(candidate.created_at).toLocaleDateString('en-GB') : null },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-start justify-between text-sm gap-2">
              <span style={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>{label}</span>
              <span className="text-right" style={{ color: '#0f172a', fontWeight: 500 }}>{value || '—'}</span>
            </div>
          ))}
          <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
            <p className="text-xs font-semibold mb-1" style={{ color: '#94a3b8' }}>RESEARCH TOPIC</p>
            <p className="text-xs leading-relaxed" style={{ color: '#475569' }}>{candidate.research_topic || '—'}</p>
          </div>
        </div>

        {/* Thesis + chapters */}
        <div className="col-span-2 space-y-4">
          {/* Progress bar */}
          <div className="rounded-2xl p-5" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-sans font-semibold text-sm" style={{ color: '#0f172a' }}>
                {thesis?.title || 'No thesis yet'}
              </p>
              <span className="font-mono text-sm font-bold" style={{ color: '#2563eb' }}>{pct}%</span>
            </div>
            <div className="w-full h-2 rounded-full overflow-hidden mb-3" style={{ background: '#f1f5f9' }}>
              <div className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, background: pct >= 100 ? '#16a34a' : '#2563eb' }} />
            </div>
            <div className="flex items-center gap-6 text-xs font-sans" style={{ color: '#64748b' }}>
              <span>{completedChapters} / {chapters.length} chapters complete</span>
              <span>{fmtWords(totalGenerated)} / {fmtWords(totalTarget)} words</span>
              <span>Last active: {relTime(lastActive(chapters))}</span>
            </div>
          </div>

          {/* Chapter timeline table */}
          <div className="rounded-2xl overflow-hidden" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
            <div className="px-5 py-3.5 font-sans font-semibold text-sm" style={{ color: '#0f172a', borderBottom: '1px solid #e2e8f0' }}>
              Chapter Progress Timeline
            </div>
            {chapters.length === 0 ? (
              <div className="text-center py-10">
                <p className="font-sans text-sm" style={{ color: '#94a3b8' }}>No chapters yet.</p>
              </div>
            ) : (
              <div>
                {[...chapters].sort((a, b) => (a.sort_order ?? a.chapter_number) - (b.sort_order ?? b.chapter_number))
                  .map((ch, i) => (
                  <div key={ch.id}>
                    <div className="flex items-center gap-3 px-5 py-3.5"
                      style={{ borderBottom: '1px solid #f8fafc' }}>
                      <span className="font-mono text-xs w-5 shrink-0 text-center" style={{ color: '#94a3b8' }}>
                        {ch.chapter_number}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-sans text-sm font-medium truncate" style={{ color: '#0f172a' }}>{ch.title}</p>
                      </div>
                      <StatusBadge status={ch.status} />
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="font-mono text-xs" style={{ color: '#64748b' }}>{fmtWords(ch.word_count)}</span>
                        <span className="font-mono text-xs" style={{ color: '#cbd5e1' }}>w</span>
                      </div>
                      <span className="font-mono text-xs shrink-0 w-16 text-right" style={{ color: '#94a3b8' }}>
                        {relTime(ch.updated_at || ch.created_at)}
                      </span>
                      {/* Action buttons */}
                      <div className="flex items-center gap-1 shrink-0">
                        {ch.status === 'DRAFT' && (
                          <button
                            onClick={() => onChapterAction(ch.id, 'APPROVED')}
                            className="p-1.5 rounded-lg transition-colors"
                            title="Approve"
                            style={{ color: '#16a34a', background: '#f0fdf4' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#dcfce7'}
                            onMouseLeave={e => e.currentTarget.style.background = '#f0fdf4'}>
                            <Check style={{ width: 12, height: 12 }} />
                          </button>
                        )}
                        {['DRAFT', 'GENERATING'].includes(ch.status) && (
                          <button
                            onClick={() => onChapterAction(ch.id, 'REVIEW')}
                            className="p-1.5 rounded-lg transition-colors"
                            title="Flag for revision"
                            style={{ color: '#d97706', background: '#fffbeb' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fffbeb'}>
                            <AlertTriangle style={{ width: 12, height: 12 }} />
                          </button>
                        )}
                        {ch.status === 'APPROVED' && (
                          <button
                            onClick={() => onChapterAction(ch.id, 'LOCKED')}
                            className="p-1.5 rounded-lg transition-colors"
                            title="Lock chapter"
                            style={{ color: '#475569', background: '#f1f5f9' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
                            onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}>
                            <Lock style={{ width: 12, height: 12 }} />
                          </button>
                        )}
                        <button
                          onClick={() => { setNoteOpen(noteOpen === ch.id ? null : ch.id); setNoteText(ch.admin_notes || '') }}
                          className="p-1.5 rounded-lg transition-colors"
                          title="Admin note"
                          style={{ color: ch.admin_notes ? '#2563eb' : '#94a3b8', background: ch.admin_notes ? '#dbeafe' : '#f8fafc' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#dbeafe'}
                          onMouseLeave={e => e.currentTarget.style.background = ch.admin_notes ? '#dbeafe' : '#f8fafc'}>
                          <MessageSquare style={{ width: 12, height: 12 }} />
                        </button>
                      </div>
                    </div>
                    {/* Note panel */}
                    {noteOpen === ch.id && (
                      <div className="px-5 pb-3.5 pt-2" style={{ background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                        <p className="font-sans text-xs font-semibold mb-2" style={{ color: '#64748b' }}>ADMIN NOTE</p>
                        <textarea
                          value={noteText}
                          onChange={e => setNoteText(e.target.value)}
                          placeholder="Add a note for this chapter…"
                          rows={2}
                          className="w-full rounded-lg px-3 py-2 font-sans text-xs outline-none resize-none"
                          style={{ background: '#fff', border: '1px solid #e2e8f0', color: '#0f172a' }}
                        />
                        <div className="flex items-center gap-2 mt-1.5">
                          <button onClick={() => handleNoteSubmit(ch.id)} disabled={saving}
                            className="px-3 py-1.5 rounded-lg font-sans text-xs font-medium transition-all"
                            style={{ background: '#0f172a', color: '#f1f5f9', opacity: saving ? 0.6 : 1 }}>
                            {saving ? 'Saving…' : 'Save Note'}
                          </button>
                          <button onClick={() => setNoteOpen(null)}
                            className="px-3 py-1.5 rounded-lg font-sans text-xs transition-all"
                            style={{ color: '#64748b' }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Generation Queue tab ───────────────────────────────────────────────────────

function GenerationQueueTab({ allChapters, allTheses, candidates, onChapterAction, onRefresh }) {
  const generating = allChapters
    .filter(c => c.status === 'GENERATING')
    .map(ch => {
      const thesis    = allTheses.find(t => t.id === ch.thesis_id)
      const candidate = candidates.find(c => c.theses.some(t => t.id === ch.thesis_id))
      return { ch, thesis, candidate }
    })

  const drafts = allChapters
    .filter(c => c.status === 'DRAFT')
    .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))
    .slice(0, 10)
    .map(ch => {
      const thesis    = allTheses.find(t => t.id === ch.thesis_id)
      const candidate = candidates.find(c => c.theses.some(t => t.id === ch.thesis_id))
      return { ch, thesis, candidate }
    })

  return (
    <div className="p-8 max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold" style={{ color: '#0f172a', fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}>
            Generation Queue
          </h1>
          <p className="font-sans text-sm mt-1" style={{ color: '#94a3b8' }}>
            {generating.length} active · {drafts.length} recently completed
          </p>
        </div>
        <button onClick={onRefresh}
          className="flex items-center gap-2 px-4 py-2 rounded-xl font-sans text-sm font-medium transition-all"
          style={{ background: '#fff', border: '1px solid #e2e8f0', color: '#475569' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#0f172a' }}
          onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#475569' }}>
          <RefreshCw style={{ width: 14, height: 14 }} /> Refresh
        </button>
      </div>

      {/* Active generations */}
      <div className="rounded-2xl overflow-hidden" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
        <div className="flex items-center gap-2 px-6 py-4" style={{ borderBottom: '1px solid #e2e8f0' }}>
          <span className="w-2 h-2 rounded-full" style={{ background: '#2563eb', animation: 'pulse 1.4s ease-in-out infinite' }} />
          <h2 className="font-sans font-semibold text-sm" style={{ color: '#0f172a' }}>Currently Generating</h2>
          <span className="font-mono text-xs ml-auto" style={{ color: '#94a3b8' }}>{generating.length} active</span>
        </div>
        {generating.length === 0 ? (
          <div className="text-center py-10">
            <Zap style={{ width: 28, height: 28, color: '#cbd5e1', margin: '0 auto 10px' }} />
            <p className="font-sans text-sm" style={{ color: '#94a3b8' }}>No generations in progress.</p>
          </div>
        ) : (
          generating.map(({ ch, thesis, candidate }, i) => (
            <div key={ch.id} className="flex items-center gap-4 px-6 py-4"
              style={{ borderBottom: i < generating.length - 1 ? '1px solid #f8fafc' : 'none' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: '#dbeafe' }}>
                <Zap style={{ width: 16, height: 16, color: '#2563eb' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-sans text-sm font-medium" style={{ color: '#0f172a' }}>
                  {candidate?.full_name || 'Unknown'} — Ch. {ch.chapter_number}: {ch.title}
                </p>
                <p className="font-sans text-xs mt-0.5 truncate" style={{ color: '#94a3b8' }}>
                  {thesis?.title || 'Unknown thesis'} · {fmtWords(ch.word_count)} words so far
                </p>
              </div>
              <StatusBadge status="GENERATING" />
              <span className="font-mono text-xs shrink-0" style={{ color: '#94a3b8' }}>
                {relTime(ch.updated_at)}
              </span>
              <button
                onClick={() => onChapterAction(ch.id, 'NOT_STARTED')}
                className="p-2 rounded-lg transition-colors"
                title="Cancel generation"
                style={{ color: '#dc2626', background: '#fee2e2' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fecaca'}
                onMouseLeave={e => e.currentTarget.style.background = '#fee2e2'}>
                <XCircle style={{ width: 14, height: 14 }} />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Recent drafts (just completed) */}
      <div className="rounded-2xl overflow-hidden" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
        <div className="px-6 py-4" style={{ borderBottom: '1px solid #e2e8f0' }}>
          <h2 className="font-sans font-semibold text-sm" style={{ color: '#0f172a' }}>Recently Generated — Awaiting Review</h2>
        </div>
        {drafts.length === 0 ? (
          <div className="text-center py-8">
            <p className="font-sans text-sm" style={{ color: '#94a3b8' }}>No drafts pending review.</p>
          </div>
        ) : (
          drafts.map(({ ch, thesis, candidate }, i) => (
            <div key={ch.id} className="flex items-center gap-4 px-6 py-3.5"
              style={{ borderBottom: i < drafts.length - 1 ? '1px solid #f8fafc' : 'none' }}>
              <div className="flex-1 min-w-0">
                <p className="font-sans text-sm font-medium" style={{ color: '#0f172a' }}>
                  {candidate?.full_name || 'Unknown'} — Ch. {ch.chapter_number}: {ch.title}
                </p>
                <p className="font-sans text-xs mt-0.5" style={{ color: '#94a3b8' }}>
                  {fmtWords(ch.word_count)} words · updated {relTime(ch.updated_at)}
                </p>
              </div>
              <StatusBadge status="DRAFT" />
              <div className="flex items-center gap-1.5">
                <button onClick={() => onChapterAction(ch.id, 'APPROVED')}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg font-sans text-xs font-medium transition-all"
                  style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#dcfce7' }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#f0fdf4' }}>
                  <Check style={{ width: 11, height: 11 }} /> Approve
                </button>
                <button onClick={() => onChapterAction(ch.id, 'REVIEW')}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg font-sans text-xs font-medium transition-all"
                  style={{ background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#fef3c7' }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fffbeb' }}>
                  <AlertTriangle style={{ width: 11, height: 11 }} /> Revise
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// ── Reviews tab ────────────────────────────────────────────────────────────────

function ReviewsTab({ allChapters, allTheses, candidates, onChapterAction }) {
  const [filterStatus, setFilterStatus] = useState('DRAFT')

  const reviewChapters = allChapters
    .filter(c => filterStatus === 'all' ? ['DRAFT', 'REVIEW'].includes(c.status) : c.status === filterStatus)
    .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))
    .map(ch => {
      const thesis    = allTheses.find(t => t.id === ch.thesis_id)
      const candidate = candidates.find(c => c.theses.some(t => t.id === ch.thesis_id))
      return { ch, thesis, candidate }
    })

  const STATUS_FILTERS = [
    { id: 'DRAFT',  label: `Draft (${allChapters.filter(c => c.status === 'DRAFT').length})` },
    { id: 'REVIEW', label: `Revision Needed (${allChapters.filter(c => c.status === 'REVIEW').length})` },
    { id: 'all',    label: 'All' },
  ]

  return (
    <div className="p-8 max-w-5xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold" style={{ color: '#0f172a', fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}>
          Chapter Reviews
        </h1>
        <p className="font-sans text-sm mt-1" style={{ color: '#94a3b8' }}>
          {allChapters.filter(c => ['DRAFT', 'REVIEW'].includes(c.status)).length} chapters awaiting action
        </p>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        {STATUS_FILTERS.map(f => (
          <button key={f.id} onClick={() => setFilterStatus(f.id)}
            className="px-3 py-1.5 rounded-lg font-sans text-xs font-medium transition-all"
            style={{
              background: filterStatus === f.id ? '#0f172a' : '#fff',
              color: filterStatus === f.id ? '#f1f5f9' : '#64748b',
              border: `1px solid ${filterStatus === f.id ? '#0f172a' : '#e2e8f0'}`,
            }}>
            {f.label}
          </button>
        ))}
      </div>

      {reviewChapters.length === 0 ? (
        <div className="rounded-2xl text-center py-14" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
          <CheckCircle2 style={{ width: 32, height: 32, color: '#86efac', margin: '0 auto 12px' }} />
          <p className="font-sans text-sm font-medium" style={{ color: '#94a3b8' }}>
            {allChapters.filter(c => ['DRAFT', 'REVIEW'].includes(c.status)).length === 0
              ? 'No chapters to review. Run migration 003_admin_access.sql if you expect data here.'
              : 'No chapters match this filter.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviewChapters.map(({ ch, thesis, candidate }) => (
            <div key={ch.id} className="rounded-2xl p-5" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: '#f1f5f9', fontSize: 13, fontWeight: 700, color: '#475569', fontFamily: 'monospace' }}>
                  {ch.chapter_number}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 flex-wrap mb-0.5">
                    <p className="font-sans font-semibold text-sm" style={{ color: '#0f172a' }}>{ch.title}</p>
                    <StatusBadge status={ch.status} />
                    {ch.admin_notes && (
                      <span className="flex items-center gap-1 font-sans text-xs" style={{ color: '#2563eb' }}>
                        <MessageSquare style={{ width: 11, height: 11 }} /> Note
                      </span>
                    )}
                  </div>
                  <p className="font-sans text-xs" style={{ color: '#64748b' }}>
                    <span className="font-medium">{candidate?.full_name || 'Unknown'}</span>
                    {' · '}{thesis?.title?.slice(0, 50) || 'Unknown thesis'}{(thesis?.title?.length || 0) > 50 ? '…' : ''}
                  </p>
                  <div className="flex items-center gap-4 mt-1.5 text-xs font-sans" style={{ color: '#94a3b8' }}>
                    <span>{fmtWords(ch.word_count)} words</span>
                    <span>Updated {relTime(ch.updated_at)}</span>
                    {ch.reviewed_by && <span>Reviewed by: {ch.reviewed_by}</span>}
                  </div>
                  {ch.admin_notes && (
                    <div className="mt-2 px-3 py-2 rounded-lg font-sans text-xs" style={{ background: '#eff6ff', color: '#2563eb', borderLeft: '3px solid #2563eb' }}>
                      {ch.admin_notes}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {ch.status === 'DRAFT' && (
                    <button onClick={() => onChapterAction(ch.id, 'APPROVED')}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl font-sans text-xs font-semibold transition-all"
                      style={{ background: '#16a34a', color: '#fff' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#15803d'}
                      onMouseLeave={e => e.currentTarget.style.background = '#16a34a'}>
                      <Check style={{ width: 12, height: 12 }} /> Approve
                    </button>
                  )}
                  {ch.status === 'REVIEW' && (
                    <button onClick={() => onChapterAction(ch.id, 'DRAFT')}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl font-sans text-xs font-semibold transition-all"
                      style={{ background: '#dbeafe', color: '#2563eb' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#bfdbfe'}
                      onMouseLeave={e => e.currentTarget.style.background = '#dbeafe'}>
                      <Play style={{ width: 12, height: 12 }} /> Back to Draft
                    </button>
                  )}
                  <button onClick={() => onChapterAction(ch.id, 'REVIEW')}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl font-sans text-xs font-medium transition-all"
                    style={{ background: '#fef3c7', color: '#d97706', border: '1px solid #fde68a' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fef9c3'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fef3c7'}>
                    <AlertTriangle style={{ width: 12, height: 12 }} /> Request Revision
                  </button>
                  {ch.status === 'APPROVED' && (
                    <button onClick={() => onChapterAction(ch.id, 'LOCKED')}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl font-sans text-xs font-medium transition-all"
                      style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
                      onMouseLeave={e => e.currentTarget.style.background = '#f1f5f9'}>
                      <Lock style={{ width: 12, height: 12 }} /> Lock
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Activity tab ───────────────────────────────────────────────────────────────

function ActivityTab({ allChapters, allTheses, candidates }) {
  const activity = [...allChapters]
    .filter(c => c.updated_at)
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
    .slice(0, 40)
    .map(ch => {
      const thesis    = allTheses.find(t => t.id === ch.thesis_id)
      const candidate = candidates.find(c => c.theses.some(t => t.id === ch.thesis_id))
      return { ch, thesis, candidate }
    })

  const STATUS_ICON = {
    GENERATING: { icon: Zap,         color: '#2563eb', bg: '#dbeafe' },
    DRAFT:      { icon: FileText,    color: '#7c3aed', bg: '#ede9fe' },
    APPROVED:   { icon: Check,       color: '#16a34a', bg: '#dcfce7' },
    REVIEW:     { icon: AlertTriangle, color: '#d97706', bg: '#fef3c7' },
    LOCKED:     { icon: Lock,        color: '#0f172a', bg: '#e2e8f0' },
    NOT_STARTED:{ icon: Circle,      color: '#94a3b8', bg: '#f1f5f9' },
  }

  return (
    <div className="p-8 max-w-4xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold" style={{ color: '#0f172a', fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}>
          Activity Log
        </h1>
        <p className="font-sans text-sm mt-1" style={{ color: '#94a3b8' }}>Derived from chapter activity across all candidates</p>
      </div>
      <div className="rounded-2xl overflow-hidden" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
        {activity.length === 0 ? (
          <div className="text-center py-12">
            <p className="font-sans text-sm" style={{ color: '#94a3b8' }}>No activity yet.</p>
          </div>
        ) : (
          activity.map(({ ch, thesis, candidate }, i) => {
            const cfg = STATUS_ICON[ch.status] || STATUS_ICON.NOT_STARTED
            const Icon = cfg.icon
            return (
              <div key={ch.id} className="flex items-start gap-4 px-6 py-3.5"
                style={{ borderBottom: i < activity.length - 1 ? '1px solid #f8fafc' : 'none' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  style={{ background: cfg.bg }}>
                  <Icon style={{ width: 13, height: 13, color: cfg.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm" style={{ color: '#0f172a' }}>
                    <span className="font-medium">{candidate?.full_name || 'Unknown'}</span>
                    {' — Ch.'}{ch.chapter_number}: {ch.title}
                  </p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <StatusBadge status={ch.status} />
                    <span className="font-sans text-xs" style={{ color: '#94a3b8' }}>
                      {fmtWords(ch.word_count)} words
                    </span>
                  </div>
                </div>
                <span className="font-mono text-xs shrink-0" style={{ color: '#cbd5e1' }}>
                  {relTime(ch.updated_at)}
                </span>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ── System tab ─────────────────────────────────────────────────────────────────

function SystemTab({ session }) {
  const [apiStatus,     setApiStatus]     = useState('checking')
  const [sbStatus,      setSbStatus]      = useState('checking')
  const [migrationOk,   setMigrationOk]   = useState(null) // null = still checking

  useEffect(() => {
    const checkApi = async () => {
      try {
        // /health is mounted at the server root, NOT under /api. Probing
        // `${API_BASE}/health` hit /api/health, which 404s, so this panel
        // reported the backend as "error" while it was perfectly healthy.
        const healthUrl = `${API_BASE.replace(/\/api\/?$/, '')}/health`
        const r = await fetch(healthUrl, { signal: AbortSignal.timeout(4000) })
        setApiStatus(r.ok ? 'online' : 'error')
      } catch { setApiStatus('offline') }
    }
    const checkSb = async () => {
      try {
        const { error } = await supabase.from('profiles').select('id', { count: 'exact', head: true })
        setSbStatus(error ? 'error' : 'online')
      } catch { setSbStatus('error') }
    }
    // Actually test whether the admin migration has run, instead of showing a
    // permanent hardcoded warning that stayed up long after it was applied.
    const checkMigration = async () => {
      try {
        const { data: { session: s } } = await supabase.auth.getSession()
        const r = await fetch(`${API_BASE}/admin/admins`, {
          headers: { Authorization: `Bearer ${s?.access_token}` },
          signal: AbortSignal.timeout(6000),
        })
        setMigrationOk(r.ok)
      } catch { setMigrationOk(false) }
    }
    checkApi()
    checkSb()
    checkMigration()
  }, [])

  const StatusDot = ({ status }) => {
    const cfg = status === 'online' ? { color: '#16a34a', label: 'Online' }
              : status === 'offline' ? { color: '#dc2626', label: 'Offline' }
              : status === 'checking' ? { color: '#d97706', label: 'Checking…' }
              : { color: '#dc2626', label: 'Error' }
    return (
      <span className="flex items-center gap-1.5 font-sans text-sm font-medium" style={{ color: cfg.color }}>
        <span className="w-2 h-2 rounded-full" style={{ background: cfg.color }} />
        {cfg.label}
      </span>
    )
  }

  const rows = [
    { label: 'Backend API',    value: <StatusDot status={apiStatus} /> },
    { label: 'Supabase',       value: <StatusDot status={sbStatus} /> },
    { label: 'Environment',    value: import.meta.env.MODE || 'production' },
    { label: 'API Base URL',   value: API_BASE },
    { label: 'Admin Email',    value: session?.user?.email || '—' },
    { label: 'Supabase URL',   value: import.meta.env.VITE_SUPABASE_URL?.replace('https://', '').split('.')[0] + '.supabase.co' },
  ]

  return (
    <div className="p-8 max-w-3xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold" style={{ color: '#0f172a', fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}>
          System Health
        </h1>
        <p className="font-sans text-sm mt-1" style={{ color: '#94a3b8' }}>Infrastructure status and configuration</p>
      </div>

      <div className="rounded-2xl overflow-hidden" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
        {rows.map(({ label, value }, i) => (
          <div key={label} className="flex items-center justify-between px-6 py-4"
            style={{ borderBottom: i < rows.length - 1 ? '1px solid #f8fafc' : 'none' }}>
            <span className="font-sans text-sm font-medium" style={{ color: '#475569' }}>{label}</span>
            <span className="font-mono text-xs" style={{ color: '#0f172a' }}>{value}</span>
          </div>
        ))}
      </div>

      {/* Migration status — checked, not assumed */}
      {migrationOk === false && (
        <div className="rounded-2xl p-5" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
          <div className="flex items-start gap-3">
            <Database style={{ width: 18, height: 18, color: '#d97706', flexShrink: 0, marginTop: 1 }} />
            <div>
              <p className="font-sans text-sm font-semibold" style={{ color: '#92400e' }}>Admin Database Migration Required</p>
              <p className="font-sans text-xs mt-1 leading-relaxed" style={{ color: '#78350f' }}>
                The admin roster could not be read. Run{' '}
                <code className="px-1 py-0.5 rounded" style={{ background: '#fef3c7' }}>021_admin_hardening.sql</code>{' '}
                in your Supabase SQL Editor to create the admin tables and functions.
              </p>
              <p className="font-mono text-xs mt-2" style={{ color: '#92400e' }}>
                Location: backend/supabase/migrations/021_admin_hardening.sql
              </p>
            </div>
          </div>
        </div>
      )}

      {migrationOk === true && (
        <div className="rounded-2xl p-4" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <div className="flex items-center gap-2.5">
            <Database style={{ width: 16, height: 16, color: '#16a34a', flexShrink: 0 }} />
            <p className="font-sans text-xs" style={{ color: '#166534' }}>
              Admin migrations applied — roster and permissions are active.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

// ── AI Cost Dashboard tab ─────────────────────────────────────────────────────

// ── Phase 10: Inference Monitor ───────────────────────────────────────────────

function InferenceMonitorTab() {
  const [status,   setStatus]   = useState(null)
  const [loading,  setLoading]  = useState(true)
  const [toggling, setToggling] = useState(false)
  const intervalRef = useRef(null)

  const load = useCallback(async () => {
    try {
      const { data: { session: s } } = await supabase.auth.getSession()
      const headers = { Authorization: `Bearer ${s?.access_token}`, 'Content-Type': 'application/json' }
      const r = await fetch(`${API_BASE}/admin/inference-status`, { headers })
      if (r.ok) setStatus(await r.json())
    } catch (err) {
      console.error('Inference status load error:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    intervalRef.current = setInterval(load, 10_000)
    return () => clearInterval(intervalRef.current)
  }, [load])

  const toggleAI = async () => {
    if (toggling || !status) return
    setToggling(true)
    try {
      const { data: { session: s } } = await supabase.auth.getSession()
      const r = await fetch(`${API_BASE}/admin/toggle-ai`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${s?.access_token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ enabled: !status.ai_enabled }),
      })
      if (r.ok) {
        const result = await r.json()
        setStatus(prev => ({ ...prev, ai_enabled: result.ai_enabled }))
        toast.success(result.message)
      } else {
        toast.error('Failed to toggle AI')
      }
    } catch {
      toast.error('Failed to toggle AI')
    } finally {
      setToggling(false)
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center p-16">
      <Loader2 style={{ width: 24, height: 24, color: '#2563eb', animation: 'spin 1s linear infinite' }} />
    </div>
  )

  const cb   = status?.circuit_breakers || {}
  const anm  = status?.anomalies || {}
  const logs = status?.recent_logs || []

  const hasAnomaly = anm.opus_detected || anm.large_tokens || anm.high_retries

  return (
    <div className="p-6 space-y-6" style={{ maxWidth: 1100 }}>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold" style={{ color: '#0f172a', fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}>
            Inference Monitor
          </h2>
          <p className="font-sans text-sm mt-0.5" style={{ color: '#64748b' }}>
            Live circuit breakers, spend rate, and anomaly detection. Refreshes every 10 s.
          </p>
        </div>
        <button
          onClick={toggleAI}
          disabled={toggling}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-semibold transition-all"
          style={{
            background: status?.ai_enabled ? '#dcfce7' : '#fee2e2',
            color:      status?.ai_enabled ? '#16a34a' : '#dc2626',
            border:     `1.5px solid ${status?.ai_enabled ? '#86efac' : '#fca5a5'}`,
          }}
        >
          {status?.ai_enabled
            ? <><ToggleRight style={{ width: 16, height: 16 }} /> AI Enabled</>
            : <><ToggleLeft  style={{ width: 16, height: 16 }} /> AI Disabled</>}
        </button>
      </div>

      {/* Anomaly alerts */}
      {hasAnomaly && (
        <div className="rounded-xl p-4 space-y-2" style={{ background: '#fef2f2', border: '1.5px solid #fca5a5' }}>
          <p className="font-sans text-xs font-bold uppercase tracking-widest" style={{ color: '#dc2626' }}>
            Anomaly Detected
          </p>
          {anm.opus_detected  && <p className="font-sans text-sm" style={{ color: '#991b1b' }}>• Opus model detected in recent logs — check for unauthorised Opus usage</p>}
          {anm.large_tokens   && <p className="font-sans text-sm" style={{ color: '#991b1b' }}>• Request with &gt;40 k input tokens in last hour</p>}
          {anm.high_retries   && <p className="font-sans text-sm" style={{ color: '#991b1b' }}>• &gt;5 failed AI requests in the last hour — possible retry storm</p>}
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Requests/min',    value: status?.requests_per_min ?? '—' },
          { label: 'Spend last hour', value: `$${(status?.spend_last_hour ?? 0).toFixed(4)}` },
          { label: 'Cache entries',   value: cb.cache_entries ?? 0 },
          { label: 'Blocked events',  value: (cb.blocked_events?.length ?? 0) },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-xl p-4" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
            <p className="font-sans text-xs" style={{ color: '#64748b' }}>{label}</p>
            <p className="font-display text-2xl font-bold mt-1" style={{ color: '#0f172a', fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}>
              {value}
            </p>
          </div>
        ))}
      </div>

      {/* Circuit breaker events */}
      {cb.blocked_events?.length > 0 && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #e2e8f0' }}>
          <div className="px-4 py-3" style={{ background: '#0f172a' }}>
            <p className="font-sans text-xs font-semibold" style={{ color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Recent blocked requests
            </p>
          </div>
          <div className="divide-y" style={{ divideColor: '#f1f5f9' }}>
            {cb.blocked_events.slice(0, 10).map((ev, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-2.5">
                <p className="font-sans text-sm" style={{ color: '#dc2626' }}>{ev.reason}</p>
                <p className="font-mono text-xs" style={{ color: '#94a3b8' }}>{relTime(ev.ts)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active thesis circuit breakers */}
      {Object.keys(cb.thesis_rates || {}).length > 0 && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #e2e8f0' }}>
          <div className="px-4 py-3" style={{ background: '#0f172a' }}>
            <p className="font-sans text-xs font-semibold" style={{ color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Active thesis call rates
            </p>
          </div>
          <div className="divide-y" style={{ divideColor: '#f1f5f9' }}>
            {Object.entries(cb.thesis_rates).map(([id, { calls, limit }]) => (
              <div key={id} className="flex items-center justify-between px-4 py-2.5">
                <p className="font-mono text-xs" style={{ color: '#475569' }}>{id.slice(0, 8)}…</p>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 rounded-full" style={{ width: 80, background: '#e2e8f0' }}>
                    <div className="h-1.5 rounded-full" style={{ width: `${Math.min(100, (calls / limit) * 100)}%`, background: calls >= limit ? '#dc2626' : '#2563eb' }} />
                  </div>
                  <p className="font-mono text-xs" style={{ color: calls >= limit ? '#dc2626' : '#475569' }}>{calls}/{limit}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent AI calls */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #e2e8f0' }}>
        <div className="px-4 py-3 flex items-center justify-between" style={{ background: '#0f172a' }}>
          <p className="font-sans text-xs font-semibold" style={{ color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Recent AI calls (last hour)
          </p>
          <p className="font-mono text-xs" style={{ color: '#475569' }}>{logs.length} rows</p>
        </div>
        {logs.length === 0 ? (
          <p className="p-4 font-sans text-sm" style={{ color: '#94a3b8' }}>No AI calls in the last hour.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full font-sans text-xs">
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {['Feature', 'Model', 'Tokens in', 'Tokens out', 'Cost', 'Trigger', 'Status', 'Time'].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-semibold" style={{ color: '#64748b' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((r, i) => {
                  const isOpus  = /opus/i.test(r.model || '')
                  const isLarge = (r.tokens_in || 0) > 40_000
                  const isFail  = !r.success
                  const rowBg   = isFail ? '#fef2f2' : isOpus || isLarge ? '#fffbeb' : i % 2 === 0 ? '#fff' : '#f8fafc'
                  return (
                    <tr key={i} style={{ background: rowBg, borderBottom: '1px solid #f1f5f9' }}>
                      <td className="px-3 py-2 font-semibold" style={{ color: '#334155' }}>{r.feature || '—'}</td>
                      <td className="px-3 py-2 font-mono" style={{ color: isOpus ? '#dc2626' : '#475569' }}>{(r.model || '—').replace('claude-', '')}</td>
                      <td className="px-3 py-2" style={{ color: isLarge ? '#dc2626' : '#475569' }}>{(r.tokens_in || 0).toLocaleString()}</td>
                      <td className="px-3 py-2" style={{ color: '#475569' }}>{(r.tokens_out || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 font-mono" style={{ color: '#475569' }}>${Number(r.cost_usd || 0).toFixed(5)}</td>
                      <td className="px-3 py-2" style={{ color: '#64748b' }}>{r.trigger_source || '—'}</td>
                      <td className="px-3 py-2">
                        {isFail
                          ? <span style={{ color: '#dc2626', fontWeight: 600 }}>✗ failed</span>
                          : <span style={{ color: '#16a34a' }}>✓</span>}
                      </td>
                      <td className="px-3 py-2" style={{ color: '#94a3b8' }}>{relTime(r.created_at)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function CostDashboardTab() {
  const [logs,      setLogs]      = useState([])
  const [loading,   setLoading]   = useState(true)
  const [error,     setError]     = useState('')
  const [period,    setPeriod]    = useState('24h') // '24h' | '7d' | '30d'

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const hours = period === '24h' ? 24 : period === '7d' ? 168 : 720
        // ai_request_logs is service-role only (RLS: USING (false)), so the
        // browser cannot read it. Querying it directly from here returned zero
        // rows every time, which is why this page rendered empty.
        const { data: { session: s } } = await supabase.auth.getSession()
        const r = await fetch(`${API_BASE}/admin/costs?hours=${hours}`, {
          headers: { Authorization: `Bearer ${s?.access_token}` },
        })
        if (!r.ok) {
          const body = await r.json().catch(() => ({}))
          throw new Error(body.error || `Request failed (${r.status})`)
        }
        const body = await r.json()
        setLogs(body.logs || [])
      } catch (err) {
        console.error('Cost load error:', err)
        setError(err.message || 'Could not load cost data.')
        setLogs([])
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [period])

  if (loading) return (
    <div className="flex items-center justify-center p-16">
      <Loader2 style={{ width: 24, height: 24, color: '#2563eb', animation: 'spin 1s linear infinite' }} />
    </div>
  )

  if (error) return (
    <div style={{ padding: '1.5rem', maxWidth: 1100 }}>
      <div className="rounded-2xl p-5" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
        <p className="font-sans text-sm font-semibold" style={{ color: '#b91c1c' }}>Could not load cost data</p>
        <p className="font-sans text-xs mt-1" style={{ color: '#7f1d1d' }}>{error}</p>
      </div>
    </div>
  )

  const totalCost    = logs.reduce((s, r) => s + Number(r.cost_usd || 0), 0)
  const totalTokensIn  = logs.reduce((s, r) => s + (r.tokens_in  || 0), 0)
  const totalTokensOut = logs.reduce((s, r) => s + (r.tokens_out || 0), 0)
  const failCount    = logs.filter(r => !r.success).length

  // Group by feature
  const byFeature = {}
  for (const r of logs) {
    const f = r.feature || 'unknown'
    if (!byFeature[f]) byFeature[f] = { count: 0, cost: 0, tokensIn: 0, tokensOut: 0 }
    byFeature[f].count++
    byFeature[f].cost       += Number(r.cost_usd || 0)
    byFeature[f].tokensIn   += r.tokens_in  || 0
    byFeature[f].tokensOut  += r.tokens_out || 0
  }

  // Group by trigger source
  const bySource = {}
  for (const r of logs) {
    const s = r.trigger_source || 'unknown'
    if (!bySource[s]) bySource[s] = { count: 0, cost: 0 }
    bySource[s].count++
    bySource[s].cost += Number(r.cost_usd || 0)
  }

  const featureRows = Object.entries(byFeature)
    .sort((a, b) => b[1].cost - a[1].cost)

  const S = { cell: { fontFamily: 'sans-serif', fontSize: 12, color: '#334155', padding: '6px 10px', borderBottom: '1px solid #f1f5f9' } }

  return (
    <div style={{ padding: '1.5rem', maxWidth: 1100 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0 }}>AI Cost Dashboard</h2>
          <p style={{ fontFamily: 'sans-serif', fontSize: 13, color: '#64748b', margin: '2px 0 0' }}>
            Every Anthropic API call logged — zero gaps.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['24h','7d','30d'].map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              style={{ padding: '4px 12px', borderRadius: 6, border: `1px solid ${period === p ? '#2563eb' : '#e2e8f0'}`,
                background: period === p ? '#2563eb' : '#fff', color: period === p ? '#fff' : '#475569',
                fontFamily: 'sans-serif', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '0.75rem', marginBottom: '1.25rem' }}>
        {[
          { label: 'Total Cost',    value: `$${totalCost.toFixed(4)}`,            color: '#dc2626' },
          { label: 'API Calls',     value: logs.length.toLocaleString(),           color: '#2563eb' },
          { label: 'Tokens In',     value: (totalTokensIn/1000).toFixed(1)  + 'k', color: '#7c3aed' },
          { label: 'Tokens Out',    value: (totalTokensOut/1000).toFixed(1) + 'k', color: '#0891b2' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background: '#fff', borderRadius: 10, padding: '1rem', border: '1px solid #e2e8f0' }}>
            <p style={{ fontFamily: 'sans-serif', fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px' }}>{label}</p>
            <p style={{ fontFamily: "'Bricolage Grotesque', system-ui, sans-serif", fontSize: 22, fontWeight: 800, color, margin: 0 }}>{value}</p>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
        {/* By feature */}
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f1f5f9' }}>
            <p style={{ fontFamily: 'sans-serif', fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>Cost by Feature</p>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Feature','Calls','Tokens','Cost'].map(h => (
                  <th key={h} style={{ ...S.cell, fontWeight: 700, color: '#64748b', textAlign: 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {featureRows.map(([feat, d]) => (
                <tr key={feat} style={{ background: '#fff' }}>
                  <td style={{ ...S.cell, fontWeight: 600 }}>{feat}</td>
                  <td style={S.cell}>{d.count}</td>
                  <td style={S.cell}>{((d.tokensIn + d.tokensOut)/1000).toFixed(1)}k</td>
                  <td style={{ ...S.cell, fontWeight: 700, color: '#dc2626' }}>${d.cost.toFixed(4)}</td>
                </tr>
              ))}
              {featureRows.length === 0 && (
                <tr><td colSpan={4} style={{ ...S.cell, color: '#94a3b8', textAlign: 'center', padding: '1.5rem' }}>
                  No logs yet. Run a generation to see data here.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* By trigger source */}
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f1f5f9' }}>
            <p style={{ fontFamily: 'sans-serif', fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>Cost by Trigger Source</p>
          </div>
          <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Object.entries(bySource).map(([src, d]) => {
              const pct = totalCost > 0 ? (d.cost / totalCost) * 100 : 0
              const srcColor = src === 'background' ? '#dc2626' : src === 'fallback' ? '#d97706' : '#2563eb'
              return (
                <div key={src}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <span style={{ fontFamily: 'sans-serif', fontSize: 12, fontWeight: 600, color: srcColor }}>{src}</span>
                    <span style={{ fontFamily: 'sans-serif', fontSize: 12, color: '#64748b' }}>{d.count} calls · ${d.cost.toFixed(4)}</span>
                  </div>
                  <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: srcColor, borderRadius: 3 }} />
                  </div>
                </div>
              )
            })}
            {Object.keys(bySource).length === 0 && (
              <p style={{ fontFamily: 'sans-serif', fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '1rem 0' }}>No data</p>
            )}
          </div>
        </div>
      </div>

      {/* Failures alert */}
      {failCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.75rem 1rem', borderRadius: 8, background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', marginBottom: '1rem' }}>
          <AlertTriangle style={{ width: 14, height: 14, color: '#dc2626', flexShrink: 0 }} />
          <p style={{ fontFamily: 'sans-serif', fontSize: 13, color: '#991b1b', margin: 0 }}>
            {failCount} failed API call{failCount > 1 ? 's' : ''} in the selected period — check error_message column for details.
          </p>
        </div>
      )}

      {/* Recent calls table */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f1f5f9' }}>
          <p style={{ fontFamily: 'sans-serif', fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
            Recent API Calls (last 50)
          </p>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Time','Feature','Model','In','Out','Cost','Source','OK'].map(h => (
                  <th key={h} style={{ ...S.cell, fontWeight: 700, color: '#64748b', textAlign: 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.slice(0, 50).map(r => (
                <tr key={r.id} style={{ background: r.success ? '#fff' : 'rgba(220,38,38,0.04)' }}>
                  <td style={{ ...S.cell, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                    {new Date(r.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </td>
                  <td style={{ ...S.cell, fontWeight: 600 }}>{r.feature}</td>
                  <td style={{ ...S.cell, fontFamily: 'monospace', fontSize: 10, color: '#64748b' }}>{(r.model || '').replace('claude-', '')}</td>
                  <td style={S.cell}>{r.tokens_in?.toLocaleString()}</td>
                  <td style={S.cell}>{r.tokens_out?.toLocaleString()}</td>
                  <td style={{ ...S.cell, fontWeight: 700, color: '#dc2626' }}>${Number(r.cost_usd||0).toFixed(5)}</td>
                  <td style={{ ...S.cell, color: r.trigger_source === 'background' ? '#dc2626' : '#64748b' }}>{r.trigger_source}</td>
                  <td style={S.cell}>{r.success ? '✓' : <span style={{ color: '#dc2626' }}>✗</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ── Settings tab ───────────────────────────────────────────────────────────────

function AdminRosterCard({ session }) {
  const [admins,  setAdmins]  = useState(null)
  const [email,   setEmail]   = useState('')
  const [busy,    setBusy]    = useState(false)
  const [error,   setError]   = useState('')
  const [notice,  setNotice]  = useState('')

  const myEmail = (session?.user?.email || '').toLowerCase()

  const authed = useCallback(async (path, opts = {}) => {
    const { data: { session: s } } = await supabase.auth.getSession()
    const r = await fetch(`${API_BASE}/admin${path}`, {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${s?.access_token}`,
        ...(opts.headers || {}),
      },
    })
    const body = await r.json().catch(() => ({}))
    if (!r.ok) throw new Error(body.error || `Request failed (${r.status})`)
    return body
  }, [])

  const load = useCallback(async () => {
    try {
      setAdmins(await authed('/admins'))
      setError('')
    } catch (err) {
      setError(err.message)
      setAdmins([])
    }
  }, [authed])

  useEffect(() => { load() }, [load])

  const add = async (e) => {
    e?.preventDefault()
    const target = email.trim().toLowerCase()
    if (!target || busy) return
    setBusy(true); setError(''); setNotice('')
    try {
      await authed('/admins', { method: 'POST', body: JSON.stringify({ email: target }) })
      setNotice(`${target} can now access the admin panel.`)
      setEmail('')
      await load()
    } catch (err) { setError(err.message) }
    finally { setBusy(false) }
  }

  const remove = async (target) => {
    if (busy) return
    if (!window.confirm(`Remove admin access for ${target}?`)) return
    setBusy(true); setError(''); setNotice('')
    try {
      await authed(`/admins/${encodeURIComponent(target)}`, { method: 'DELETE' })
      setNotice(`${target} no longer has admin access.`)
      await load()
    } catch (err) { setError(err.message) }
    finally { setBusy(false) }
  }

  return (
    <div className="rounded-2xl overflow-hidden mb-6" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
      <div className="px-6 py-4" style={{ borderBottom: '1px solid #f1f5f9' }}>
        <p className="font-sans text-sm font-semibold" style={{ color: '#0f172a' }}>Administrators</p>
        <p className="font-sans text-xs mt-0.5" style={{ color: '#94a3b8' }}>
          Only these accounts can open the admin panel or call the admin API.
        </p>
      </div>

      <form onSubmit={add} className="flex gap-2 px-6 py-4" style={{ borderBottom: '1px solid #f1f5f9' }}>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="new.admin@example.com"
          disabled={busy}
          className="flex-1 px-3 py-2 rounded-lg font-sans text-sm outline-none"
          style={{ border: '1px solid #e2e8f0', color: '#0f172a', background: '#f8fafc' }}
        />
        <button
          type="submit"
          disabled={busy || !email.trim()}
          className="px-4 py-2 rounded-lg font-sans text-sm font-semibold disabled:opacity-50"
          style={{ background: '#2563eb', color: '#fff', border: 'none', cursor: busy || !email.trim() ? 'not-allowed' : 'pointer' }}
        >
          Add admin
        </button>
      </form>

      {(error || notice) && (
        <div className="px-6 py-3" style={{ borderBottom: '1px solid #f1f5f9' }}>
          {error  && <p className="font-sans text-xs" style={{ color: '#b91c1c' }}>{error}</p>}
          {notice && <p className="font-sans text-xs" style={{ color: '#15803d' }}>{notice}</p>}
        </div>
      )}

      {admins === null ? (
        <div className="px-6 py-5">
          <Loader2 style={{ width: 16, height: 16, color: '#2563eb', animation: 'spin 1s linear infinite' }} />
        </div>
      ) : admins.length === 0 ? (
        <p className="px-6 py-5 font-sans text-xs" style={{ color: '#94a3b8' }}>No administrators found.</p>
      ) : (
        admins.map((a, i) => (
          <div key={a.email} className="flex items-center justify-between gap-4 px-6 py-3"
            style={{ borderBottom: i < admins.length - 1 ? '1px solid #f8fafc' : 'none' }}>
            <div>
              <p className="font-mono text-xs" style={{ color: '#0f172a' }}>
                {a.email}
                {a.email === myEmail && (
                  <span className="ml-2 px-1.5 py-0.5 rounded font-sans"
                    style={{ fontSize: 10, background: '#eff6ff', color: '#1d4ed8' }}>you</span>
                )}
              </p>
              {a.added_by && (
                <p className="font-sans mt-0.5" style={{ fontSize: 10, color: '#94a3b8' }}>added by {a.added_by}</p>
              )}
            </div>
            <button
              onClick={() => remove(a.email)}
              disabled={busy || a.email === myEmail}
              title={a.email === myEmail ? 'You cannot remove your own access' : 'Remove admin access'}
              className="px-2.5 py-1 rounded-lg font-sans text-xs disabled:opacity-40"
              style={{
                border: '1px solid #fecaca', color: '#b91c1c', background: '#fff',
                cursor: busy || a.email === myEmail ? 'not-allowed' : 'pointer',
              }}
            >
              Remove
            </button>
          </div>
        ))
      )}
    </div>
  )
}

function SettingsTab({ session }) {
  return (
    <div className="p-8 max-w-2xl">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold" style={{ color: '#0f172a', fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}>
          Admin Settings
        </h1>
        <p className="font-sans text-sm mt-1" style={{ color: '#94a3b8' }}>Platform configuration</p>
      </div>

      <AdminRosterCard session={session} />

      <div className="rounded-2xl overflow-hidden" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
        {[
          { label: 'API Base URL',     value: API_BASE },
          { label: 'Supabase Project', value: import.meta.env.VITE_SUPABASE_URL?.split('.')[0]?.replace('https://', '') || '—' },
          { label: 'Environment',      value: import.meta.env.MODE || 'production' },
        ].map(({ label, value }, i, arr) => (
          <div key={label} className="flex items-start justify-between gap-4 px-6 py-4"
            style={{ borderBottom: i < arr.length - 1 ? '1px solid #f8fafc' : 'none' }}>
            <span className="font-sans text-sm font-medium" style={{ color: '#475569' }}>{label}</span>
            <span className="font-mono text-xs text-right max-w-sm" style={{ color: '#0f172a', wordBreak: 'break-all' }}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Root AdminPage ─────────────────────────────────────────────────────────────

export default function AdminPage() {
  const navigate  = useNavigate()
  const location  = useLocation()

  const [session,     setSession]     = useState(null)
  const [checking,    setChecking]    = useState(true)
  const [loading,     setLoading]     = useState(false)
  const [accessErr,   setAccessErr]   = useState('')

  // Raw data
  const [allProfiles, setAllProfiles] = useState([])
  const [allTheses,   setAllTheses]   = useState([])
  const [allChapters, setAllChapters] = useState([])

  // UI state
  const [selectedCandidate, setSelectedCandidate] = useState(null)

  // ── Auth check ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const check = async () => {
      const { data: { session: s } } = await supabase.auth.getSession()
      if (!s) { navigate('/admin/login'); return }
      const email = s.user?.email || ''

      // The server decides. The old check consulted VITE_ADMIN_EMAILS, which is
      // both unset (so it admitted everyone) and client-side, so it was never a
      // real control. The API enforces this independently either way.
      try {
        const r = await fetch(`${API_BASE}/admin/me`, {
          headers: { Authorization: `Bearer ${s.access_token}` },
        })
        if (!r.ok) {
          setChecking(false)
          setAccessErr(`Access denied. ${email} is not an administrator.`)
          return
        }
      } catch {
        setChecking(false)
        setAccessErr('Could not verify admin access — the backend API is unreachable.')
        return
      }

      setSession(s)
      setChecking(false)
      loadAllData()
    }
    check()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Data loading ─────────────────────────────────────────────────────────────
  const loadAllData = useCallback(async () => {
    setLoading(true)
    try {
      // Try backend admin API first
      let backendProfiles = null
      let backendTheses   = null
      let backendChapters = null
      try {
        const { data: { session: s } } = await supabase.auth.getSession()
        const headers = { Authorization: `Bearer ${s?.access_token}`, 'Content-Type': 'application/json' }
        const [pRes, tRes, cRes] = await Promise.allSettled([
          fetch(`${API_BASE}/admin/candidates`, { headers }).then(r => r.ok ? r.json() : null),
          fetch(`${API_BASE}/admin/theses`,     { headers }).then(r => r.ok ? r.json() : null),
          fetch(`${API_BASE}/admin/chapters`,   { headers }).then(r => r.ok ? r.json() : null),
        ])
        if (pRes.status === 'fulfilled' && Array.isArray(pRes.value)) backendProfiles = pRes.value
        if (tRes.status === 'fulfilled' && Array.isArray(tRes.value)) backendTheses   = tRes.value
        if (cRes.status === 'fulfilled' && Array.isArray(cRes.value)) backendChapters = cRes.value
      } catch { /* backend unavailable */ }

      // Supabase direct fallback (requires 003_admin_access.sql)
      const [{ data: profiles }, { data: theses }, { data: chapters }] = await Promise.all([
        backendProfiles ? { data: backendProfiles } :
          supabase.from('profiles').select('*').order('created_at', { ascending: false }),
        backendTheses ? { data: backendTheses } :
          supabase.from('theses').select('*').order('created_at', { ascending: false }),
        backendChapters ? { data: backendChapters } :
          supabase.from('chapters')
            .select('id, thesis_id, chapter_number, title, word_count, status, target_words, user_notes, admin_notes, reviewed_by, reviewed_at, sort_order, created_at, updated_at')
            .order('sort_order', { ascending: true }),
      ])

      setAllProfiles(profiles || [])
      setAllTheses(theses   || [])
      setAllChapters(chapters || [])
    } catch (err) {
      console.error('Admin data load error:', err)
      toast.error('Failed to load admin data')
    } finally {
      setLoading(false)
    }
  }, [])

  // ── Derived candidates structure ─────────────────────────────────────────────
  const candidates = allProfiles.map(p => {
    const theses      = allTheses.filter(t => t.user_id === p.id)
    const allChs      = allChapters.filter(ch => theses.some(t => t.id === ch.thesis_id))
    return { ...p, theses: theses.map(t => ({ ...t, chapters: allChapters.filter(ch => ch.thesis_id === t.id) })), allChapters: allChs }
  })

  // ── Chapter action (approve/reject/lock/etc.) ─────────────────────────────────
  const handleChapterAction = useCallback(async (chapterId, newStatus) => {
    const old = allChapters.find(c => c.id === chapterId)
    if (!old) return

    // Optimistic update
    setAllChapters(prev => prev.map(c => c.id === chapterId ? { ...c, status: newStatus } : c))

    // Try backend admin route first
    let success = false
    try {
      const { data: { session: s } } = await supabase.auth.getSession()
      const r = await fetch(`${API_BASE}/admin/chapters/${chapterId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${s?.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      })
      if (r.ok) success = true
    } catch { /* fall through */ }

    if (!success) {
      // Try SECURITY DEFINER RPC (bypasses RLS — requires 003b_admin_rpc.sql)
      const { error: rpcErr } = await supabase.rpc('admin_set_chapter_status', {
        p_chapter_id: chapterId,
        p_status: newStatus,
      })

      if (rpcErr) {
        // Rollback
        setAllChapters(prev => prev.map(c => c.id === chapterId ? { ...c, status: old.status } : c))
        toast.error('Admin write failed. Run 003b_admin_rpc.sql in Supabase SQL Editor.')
        return
      }
      success = true
    }

    if (success) {
      const label = CH_STATUS[newStatus]?.label || newStatus
      toast.success(`Chapter marked as ${label}`)
    }
  }, [allChapters])

  // ── Navigation helper ─────────────────────────────────────────────────────────
  const handleNavigate = (path) => {
    window.history.pushState({}, '', path)
    window.dispatchEvent(new PopStateEvent('popstate'))
    setSelectedCandidate(null)
  }

  // ── Loading / access denied screens ──────────────────────────────────────────
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f1f5f9' }}>
        <Loader2 style={{ width: 28, height: 28, color: '#2563eb', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }`}</style>
      </div>
    )
  }

  if (accessErr) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: '#f1f5f9' }}>
        <Shield style={{ width: 40, height: 40, color: '#dc2626' }} />
        <p className="font-display text-lg font-semibold" style={{ color: '#0f172a', fontFamily: "'Bricolage Grotesque', system-ui, sans-serif" }}>Access Denied</p>
        <p className="font-sans text-sm" style={{ color: '#64748b' }}>{accessErr}</p>
        <button onClick={() => navigate('/')} className="mt-2 font-sans text-sm underline" style={{ color: '#2563eb' }}>
          Back to App
        </button>
      </div>
    )
  }

  const handleLogout = async () => { await supabase.auth.signOut(); navigate('/login') }

  // ── Render ────────────────────────────────────────────────────────────────────
  const path = location.pathname

  const renderContent = () => {
    // Candidates detail sub-view
    if (selectedCandidate && path.startsWith('/admin/candidates')) {
      return (
        <ThesisDetailView
          candidate={selectedCandidate}
          allTheses={allTheses}
          onBack={() => setSelectedCandidate(null)}
          onChapterAction={handleChapterAction}
        />
      )
    }

    if (path === '/admin' || path === '/admin/') {
      return <OverviewTab candidates={candidates} allChapters={allChapters} allTheses={allTheses} onNavigate={handleNavigate} />
    }
    if (path.startsWith('/admin/candidates')) {
      return <CandidatesTab candidates={candidates} onSelect={setSelectedCandidate} />
    }
    if (path.startsWith('/admin/queue')) {
      return <GenerationQueueTab allChapters={allChapters} allTheses={allTheses} candidates={candidates} onChapterAction={handleChapterAction} onRefresh={loadAllData} />
    }
    if (path.startsWith('/admin/reviews')) {
      return <ReviewsTab allChapters={allChapters} allTheses={allTheses} candidates={candidates} onChapterAction={handleChapterAction} />
    }
    if (path.startsWith('/admin/activity')) {
      return <ActivityTab allChapters={allChapters} allTheses={allTheses} candidates={candidates} />
    }
    if (path.startsWith('/admin/costs')) {
      return <CostDashboardTab />
    }
    if (path.startsWith('/admin/monitor')) {
      return <InferenceMonitorTab />
    }
    if (path.startsWith('/admin/system')) {
      return <SystemTab session={session} />
    }
    if (path.startsWith('/admin/settings')) {
      return <SettingsTab session={session} />
    }
    return (
      <div className="p-8">
        <p className="font-sans text-sm" style={{ color: '#94a3b8' }}>Coming soon.</p>
      </div>
    )
  }

  return (
    <AdminLayout session={session} onLogout={handleLogout}>
      {loading && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2 rounded-xl shadow-lg font-sans text-sm"
          style={{ background: '#0f172a', color: '#f1f5f9' }}>
          <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />
          Loading data…
        </div>
      )}
      {renderContent()}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes pulse { 0%,100% { opacity:1 } 50% { opacity:0.4 } }`}</style>
    </AdminLayout>
  )
}
